import React, { useState, useEffect } from "react";
import FormBottomRow from "~/components/form-bottom-row";
import {
  ChargeDatePicker,
  ChargeEditNotes,
  ChargeEeCodePicker,
  ChargeEmployeePicker,
  ChargeInvoiceNotes,
  ChargeProjectField,
  ChargeQuantityField,
  ChargeRateAmountField,
  ChargeRateField,
  ChargeRateUnitField,
  CustomRateLabelField,
} from "./EditChargeFields";
import {
  ChargeBillableToggle,
  ChargeOverrideRateToggle,
  ChargeTaxableToggle,
} from "./EditChargeToggles";
import Spinner from "~/components/spinner";
import styled from "@emotion/styled";
import { useChargeFormStateApi } from "./chargeFormProviders/ChargeFormStateProvider";
import { ChargeFormFieldsProps } from "./types";
import { useRateSheet } from "./chargeFormProviders/RateSheetProvider";
import { useRateInputsModified } from "./hooks/useRateInputsModified";
import { ExpenseRate } from "~/gql/ExpenseRate";
import { TaskRate } from "~/gql/types";

const FormRow = styled("div")`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
`;

export const ChargeFormFields: React.FC<ChargeFormFieldsProps> = ({
  error,
  values,
  onCancel,
}) => {
  const [rateSuggestions, setRateSuggestions] = useState<
    ExpenseRate[] | TaskRate[] | null
  >(null);

  const { fieldStates } = useChargeFormStateApi();
  const {
    rateValidationMessage,
    rateAmountValidationMessage,
    handleRateSuggestions,
  } = useRateSheet();
  const rateInputsModified = useRateInputsModified();

  useEffect(() => {
    async function getRateSuggestions() {
      const res = await handleRateSuggestions(values, rateInputsModified);
      setRateSuggestions(res);
    }
    void getRateSuggestions();
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.useCustomRate,
    values.date,
    values.project,
    values.rate,
    values.employee,
  ]);

  return (
    <div>
      <FormRow>
        {fieldStates.date.visible && <ChargeDatePicker />}
        {fieldStates.employee.visible && <ChargeEmployeePicker />}
      </FormRow>

      <FormRow>
        <ChargeBillableToggle />
        {fieldStates.eeCode.visible && <ChargeEeCodePicker />}
      </FormRow>

      <FormRow>
        {fieldStates.taxable.visible && <ChargeTaxableToggle />}
        <ChargeQuantityField />
      </FormRow>

      <FormRow>
        {fieldStates.project.visible && <ChargeProjectField {...values.date} />}
        <ChargeRateAmountField
          rateAmountValidationMessage={rateAmountValidationMessage.current}
        />
      </FormRow>

      <FormRow>
        <ChargeOverrideRateToggle />
        <ChargeRateUnitField {...fieldStates.unit} />
        <ChargeInvoiceNotes />
      </FormRow>

      <FormRow>
        {fieldStates.customRateLabel.visible && (
          <CustomRateLabelField customRateLabel={fieldStates.customRateLabel} />
        )}
        {fieldStates.rate.visible && (
          <ChargeRateField
            rate={fieldStates.rate}
            rateValidationMessage={rateValidationMessage.current}
            rateSuggestions={rateSuggestions}
            type={values.type}
          />
        )}
        <div></div>
        {fieldStates.editNotes.visible && <ChargeEditNotes />}
      </FormRow>

      <FormBottomRow
        errorMessage={error}
        buttonText="Save"
        onCancel={() => {
          onCancel();
        }}
      />

      {/* replace with loading states */}
      <Spinner open={false} />
    </div>
  );
};
